import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { SectionResult } from '../types';
import { fetchDocument } from '../api';
import { errorMessage } from '../errors';
import ReferencesGrid from './ReferencesGrid';
import './SectionResults.css';

interface SectionResultsProps {
  sections: SectionResult[];
  loading: boolean;
  currentSection: string | null;
  error: string | null;
  selectedTail?: string;
}

/** Modal state for the PDF document viewer — same shape as fetchDocument result. */
type ModalDoc = Awaited<ReturnType<typeof fetchDocument>>;

/** Inline SVG icons for the modal toolbar buttons. */
const maximizeIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

const minimizeIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
  </svg>
);

/** Displays analysis sections with a PDF reference viewer modal. */
const SectionResults: React.FC<SectionResultsProps> = ({
  sections,
  loading,
  currentSection,
  error,
  selectedTail,
}) => {
  const [modalDoc, setModalDoc] = useState<ModalDoc | null>(null);
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [maximized, setMaximized] = useState<boolean>(false);

  const allSourceUris = sections.flatMap((section) => section.sourceUris || []);
  const uniqueUris = Array.from(new Set(allSourceUris));

  /** Open a document from S3 in the PDF viewer modal. */
  const openDocument = async (uri: string): Promise<void> => {
    setDocLoading(true);
    setDocError(null);
    setModalDoc(null);
    try {
      const doc = await fetchDocument(uri);
      setModalDoc(doc);
    } catch (err: unknown) {
      setDocError(errorMessage(err));
    } finally {
      setDocLoading(false);
    }
  };

  /** Close the PDF viewer modal and reset state. */
  const closeModal = (): void => {
    setModalDoc(null);
    setDocError(null);
    setMaximized(false);
  };

  if (error && sections.length === 0) {
    return (
      <div className="results-content">
        <div className="error-banner" role="alert">&#9888; {error}</div>
      </div>
    );
  }

  if (!loading && sections.length === 0) {
    return (
      <div className="results-content">
        <div className="empty-state">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-2h2v-4h4v-2h-4V7h-2v4H8v2h4z" />
          </svg>
          <p>Select a tail number and click Run Analysis</p>
        </div>
      </div>
    );
  }

  // Prefer the already-loaded section title for the loading label; fall back
  // to the raw section ID when the section hasn't arrived yet.
  const loadingTitle = currentSection
    ? sections.find((s) => s.section === currentSection)?.title ?? `Section ${currentSection}`
    : '';

  return (
    <div className="results-content">
      {sections.map((sec) => (
        <div key={sec.section} className="section-block">
          <h2 className="section-title">
            {sec.section}. {sec.title}
          </h2>
          <div className="section-body">
            {sec.section === '9' ? (
              <ReferencesGrid
                content={sec.content}
                sourceUris={uniqueUris}
                onOpenDocument={openDocument}
              />
            ) : (
              <ReactMarkdown>{sec.content}</ReactMarkdown>
            )}
          </div>
        </div>
      ))}

      {loading && currentSection && (
        <div className="section-loading">
          <div className="spinner" role="status" aria-label="Loading" />
          <p>
            Analyzing {selectedTail} — Section {currentSection}: {loadingTitle}...
          </p>
        </div>
      )}

      {(modalDoc || docLoading || docError) && (
        <div className="ref-modal-overlay" onClick={closeModal}>
          <div
            className={`ref-modal ref-modal--pdf ${maximized ? 'ref-modal--maximized' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ref-modal-toolbar">
              <h3 className="ref-modal-title">
                {modalDoc?.filename || 'Loading...'}
              </h3>
              <div className="ref-modal-toolbar-btns">
                <button
                  className="ref-modal-btn"
                  onClick={() => setMaximized(!maximized)}
                  aria-label={maximized ? 'Minimize' : 'Maximize'}
                  title={maximized ? 'Minimize' : 'Maximize'}
                >
                  {maximized ? minimizeIcon : maximizeIcon}
                </button>
                <button
                  className="ref-modal-btn ref-modal-btn--close"
                  onClick={closeModal}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
            {docLoading ? (
              <div className="ref-modal-loading">
                <div className="spinner" />
                <p>Loading document...</p>
              </div>
            ) : docError ? (
              <div className="ref-modal-error">&#9888; {docError}</div>
            ) : modalDoc ? (
              <iframe
                className="ref-modal-pdf-frame"
                src={`${modalDoc.url}#toolbar=1&navpanes=1`}
                title={modalDoc.filename}
                key={modalDoc.url}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionResults;
